import { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowSquareOut, FileText, Shield, CheckCircle } from "@phosphor-icons/react";
import { LogoMark } from "@/components/Logo";

/**
 * Blocking modal shown after login when the user has not accepted
 * the Terms & Conditions and Privacy Policy. Legally gate the entire app.
 */
export default function TermsAcceptModal({ onAccepted }) {
  const { setUser } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [openedT, setOpenedT] = useState(false);
  const [openedP, setOpenedP] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = agreed && openedT && openedP && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-terms");
      setUser(data);
      toast.success("Gracias! Terminos aceptados");
      onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la aceptacion");
    } finally {
      setSubmitting(false);
    }
  };

  const openPdf = (href, setter) => {
    setter(true);
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="terms-accept-modal"
    >
      <div className="relative w-full max-w-xl bg-[#09090B] border border-[#27272A] rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* subtle brand glow */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(94,106,210,0.18), transparent 60%)",
          }}
        />
        <div className="relative p-7">
          <div className="flex items-center gap-3 mb-5">
            <LogoMark size={44} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Antes de continuar</div>
              <h2 className="font-display text-xl font-medium tracking-tight">
                Acepta los Terminos y la Politica de Privacidad
              </h2>
            </div>
          </div>

          <p className="text-sm text-[#A1A1AA] leading-relaxed mb-5">
            Para usar Wazco Express debes leer y aceptar nuestros documentos legales.
            Abre cada uno (botones abajo) y confirma tu aceptacion.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <button
              type="button"
              onClick={() => openPdf("/legal/terminos.pdf", setOpenedT)}
              data-testid="open-terms-pdf"
              className={`group relative flex items-center gap-3 p-4 rounded-lg border transition-all text-left ${
                openedT
                  ? "border-[#5E6AD2] bg-[#5E6AD2]/10"
                  : "border-[#27272A] bg-[#121214] hover:border-[#3F3F46]"
              }`}
            >
              <div className="h-10 w-10 rounded-md bg-[#5E6AD2]/15 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-[#5E6AD2]" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  Terminos y Condiciones
                  {openedT && <CheckCircle size={12} weight="fill" className="text-[#22C55E]" />}
                </div>
                <div className="text-[11px] text-[#71717A] flex items-center gap-1 mt-0.5">
                  Abrir PDF <ArrowSquareOut size={10} />
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => openPdf("/legal/privacidad.pdf", setOpenedP)}
              data-testid="open-privacy-pdf"
              className={`group relative flex items-center gap-3 p-4 rounded-lg border transition-all text-left ${
                openedP
                  ? "border-[#F97316] bg-[#F97316]/10"
                  : "border-[#27272A] bg-[#121214] hover:border-[#3F3F46]"
              }`}
            >
              <div className="h-10 w-10 rounded-md bg-[#F97316]/15 flex items-center justify-center shrink-0">
                <Shield size={20} className="text-[#F97316]" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  Politica de Privacidad
                  {openedP && <CheckCircle size={12} weight="fill" className="text-[#22C55E]" />}
                </div>
                <div className="text-[11px] text-[#71717A] flex items-center gap-1 mt-0.5">
                  Abrir PDF <ArrowSquareOut size={10} />
                </div>
              </div>
            </button>
          </div>

          <label
            className="flex items-start gap-3 p-4 rounded-lg border border-[#27272A] bg-[#121214] cursor-pointer select-none hover:border-[#3F3F46] transition-colors mb-5"
            data-testid="terms-agree-label"
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={!openedT || !openedP}
              data-testid="terms-agree-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-[#3F3F46] bg-[#09090B] text-[#5E6AD2] focus:ring-[#5E6AD2] focus:ring-offset-0 disabled:opacity-40"
            />
            <span className="text-sm text-[#D4D4D8] leading-snug">
              He leido y acepto los <b className="text-white">Terminos y Condiciones</b> y la{" "}
              <b className="text-white">Politica de Privacidad</b> de Wazco Express.
              {(!openedT || !openedP) && (
                <span className="block text-[11px] text-[#71717A] mt-1">
                  Abre ambos documentos para habilitar esta casilla.
                </span>
              )}
            </span>
          </label>

          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="terms-accept-button"
            className="w-full h-11 bg-[#5E6AD2] hover:bg-[#4F58B8] text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Guardando..." : "Aceptar y continuar"}
          </Button>

          <p className="text-[11px] text-[#71717A] text-center mt-4 leading-relaxed">
            Al continuar confirmas que tienes al menos 18 anos y que los datos de tu negocio
            son veridicos. Puedes consultar los documentos cuando quieras desde tu cuenta.
          </p>
        </div>
      </div>
    </div>
  );
}
